import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { Card, CardContent } from "./ui/card";

/**
 * Iter-70: CollapsibleCard — a self-contained click-to-expand card.
 *
 * Drop-in replacement for the common <Card><CardHeader/><CardContent/></Card>
 * pattern used on Settings.jsx. Default state is COLLAPSED to keep the page
 * compact (user request iter-70).
 *
 * Props:
 *  - title: ReactNode rendered inside the clickable header
 *  - icon: optional Lucide icon component (e.g. `Users`)
 *  - iconColor: optional tailwind text colour class for the icon
 *  - badge: optional small ReactNode shown to the right of the title
 *  - defaultOpen: override the default collapsed state
 *  - className: forwarded to the outer <Card>
 *  - children: card body content
 *  - testId: data-testid for the trigger button
 */
const CollapsibleCard = ({
  title,
  icon: Icon,
  iconColor = "text-primary",
  badge,
  defaultOpen = false,
  className = "",
  children,
  testId,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card className={`glass-card overflow-hidden ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-6 py-4 hover:bg-white/[0.02] transition-colors"
        aria-expanded={open}
        data-testid={testId}
      >
        <div className="flex items-center gap-2 font-heading text-base">
          {Icon ? <Icon className={`w-5 h-5 ${iconColor}`} /> : null}
          <span>{title}</span>
          {badge}
        </div>
        <ChevronDown
          className={`w-4 h-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="border-t border-white/5">
          <CardContent className="p-6">{children}</CardContent>
        </div>
      )}
    </Card>
  );
};

export default CollapsibleCard;
